import { components } from "@/api-schema";
import { VehicleBlockReason } from "@/api-schema-enums";
import { useAbility } from "@/casl-provider";
import { useVehicleBlockCreateMutation } from "@/common/api/vehicle-block/use-vehicle-block-create.mutation";
import { useVehicleBlockDeleteMutation } from "@/common/api/vehicle-block/use-vehicle-block-delete.mutation";
import { toastMutationStatus } from "@/common/helpers/toast-mutation-status";
import { useAppFeatureFlag } from "@/common/hooks/use-app-feature-flag";
import type { DetailAction } from "@/components/detail-page/types";
import { FeatureFlag } from "@/config/feature-flags";
import { vehicleHasOwningBranch } from "@/common/helpers/vehicle-has-owning-branch";
import { vehicleHasPendingRelocationOrder } from "@/common/helpers/vehicle-has-pending-relocation-order";
import { VehicleBlockDialog } from "@/components/vehicle-block/vehicle-block-dialog";
import { createVehicleBlockPayload } from "@/components/vehicle-block/_helpers/vehicle-block-payload";
import { vehicleIsBlockable } from "@/common/helpers/vehicle-is-blockable";
import { userStore } from "@/store/user-store";
import { CalculatorIcon, LockIcon, LockOpenIcon, MapPinPenIcon } from "lucide-react";
import { JSX, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "wouter";

type Vehicle = components["schemas"]["Vehicle"];

interface UseVehicleActionsReturn {
  getActionsForVehicle: (vehicle: Vehicle) => DetailAction[];
  dialogs: JSX.Element;
}

interface UseVehicleActionsOptions {
  showRelocationOrderAction?: boolean;
  showVehicleValuationAction?: boolean;
}

export function useVehicleActions(options: UseVehicleActionsOptions = {}): UseVehicleActionsReturn {
  const { t } = useTranslation(["common", "masterdata", "fab", "fit"]);
  const [_, navigate] = useLocation();
  const { user } = userStore();
  const ability = useAbility();

  const canManageVehicleBlock = ability.can("manage", "VehicleBlock");
  const canCreateVehicleValuation = ability.can("create", "VehicleValuation");
  const canCreateRelocationOrder = ability.can("create", "RelocationOrder");

  const { value: isFitEnabled } = useAppFeatureFlag(FeatureFlag.FitSidebar);
  const showRelocationOrderAction = options.showRelocationOrderAction ?? isFitEnabled;
  const showVehicleValuationAction = options.showVehicleValuationAction ?? true;

  const [blockDialogOpen, setBlockDialogOpen] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null);
  const [blockReason, setBlockReason] = useState<VehicleBlockReason>(VehicleBlockReason.Maintenance);
  const [blockDuration, setBlockDuration] = useState<number>(1);
  const [blockedByUserUuid, setBlockedByUserUuid] = useState<string>(user?.uuid ?? "");

  const { mutateAsync: createVehicleBlock, isPending: isBlockingVehicle } = useVehicleBlockCreateMutation();
  const { mutateAsync: deleteVehicleBlock } = useVehicleBlockDeleteMutation();

  const handleBlockVehicleClick = useCallback(
    (vehicle: Vehicle) => {
      setSelectedVehicle(vehicle);
      setBlockReason(VehicleBlockReason.Maintenance);
      setBlockDuration(1);
      setBlockedByUserUuid(user?.uuid ?? "");
      setBlockDialogOpen(true);
    },
    [user?.uuid],
  );

  const handleCancelBlockVehicle = useCallback(() => {
    setBlockDialogOpen(false);
    setSelectedVehicle(null);
  }, []);

  const handleConfirmBlockVehicle = useCallback(async () => {
    if (!selectedVehicle || !blockedByUserUuid) return;

    const payload = createVehicleBlockPayload(selectedVehicle.uuid, blockedByUserUuid, blockReason, blockDuration);

    await toastMutationStatus({
      mutationFn: () => createVehicleBlock(payload),
      successMessage: t("masterdata:VehicleSuccessfullyBlocked"),
      errorMessage: t("masterdata:ErrorBlockingVehicle"),
    });

    setBlockDialogOpen(false);
    setSelectedVehicle(null);
  }, [selectedVehicle, blockedByUserUuid, blockReason, blockDuration, createVehicleBlock, t]);

  const handleUnblockVehicle = useCallback(
    async (vehicle: Vehicle) => {
      if (!vehicle.vehicleBlock?.uuid) return;

      await toastMutationStatus({
        mutationFn: () => deleteVehicleBlock({ uuid: vehicle.vehicleBlock?.uuid }),
        successMessage: t("masterdata:VehicleSuccessfullyUnblocked"),
        errorMessage: t("masterdata:ErrorUnblockingVehicle"),
      });
    },
    [deleteVehicleBlock, t],
  );

  const handleRequestTransport = useCallback(
    (vehicle: Vehicle) => {
      navigate(`~/fit/relocation-order/init/select-order-type?vehicleUuid=${vehicle.uuid}`);
    },
    [navigate],
  );

  const handleStartVehicleValuation = useCallback(
    (vehicle: Vehicle) => {
      navigate(`~/fab/list/create?vehicleUuid=${vehicle.uuid}`);
    },
    [navigate],
  );

  const getActionsForVehicle = useCallback(
    (vehicle: Vehicle): DetailAction[] => {
      const actions: DetailAction[] = [];

      const isBlocked = vehicle.vehicleBlock !== null && vehicle.vehicleBlock !== undefined;
      const hasPendingRelocationOrder = vehicleHasPendingRelocationOrder(vehicle);

      if (showRelocationOrderAction) {
        if (canCreateRelocationOrder && vehicleHasOwningBranch(vehicle) && !hasPendingRelocationOrder) {
          actions.push({
            id: "request-transport",
            label: t("common:RequestTransport"),
            icon: MapPinPenIcon,
            onClick: () => handleRequestTransport(vehicle),
          });
        }
      }

      if (showVehicleValuationAction && canCreateVehicleValuation) {
        actions.push({
          id: "start-vehicle-valuation",
          label: t("fab:StartVehicleValuation"),
          icon: CalculatorIcon,
          onClick: () => handleStartVehicleValuation(vehicle),
        });
      }

      if (canManageVehicleBlock && !isBlocked && vehicleIsBlockable(vehicle)) {
        actions.push({
          id: "block-vehicle",
          label: t("masterdata:BlockVehicle"),
          icon: LockIcon,
          onClick: () => handleBlockVehicleClick(vehicle),
        });
      } else if (canManageVehicleBlock && isBlocked) {
        actions.push({
          id: "unblock-vehicle",
          label: t("masterdata:UnblockVehicle"),
          icon: LockOpenIcon,
          onClick: () => handleUnblockVehicle(vehicle),
        });
      }

      return actions;
    },
    [
      showRelocationOrderAction,
      showVehicleValuationAction,
      canCreateRelocationOrder,
      canCreateVehicleValuation,
      canManageVehicleBlock,
      t,
      handleRequestTransport,
      handleStartVehicleValuation,
      handleBlockVehicleClick,
      handleUnblockVehicle,
    ],
  );

  const dialogs = useMemo(
    () => (
      <VehicleBlockDialog
        open={blockDialogOpen}
        onOpenChange={setBlockDialogOpen}
        blockReason={blockReason}
        onBlockReasonChange={setBlockReason}
        blockDuration={blockDuration}
        onBlockDurationChange={setBlockDuration}
        blockedByUserUuid={blockedByUserUuid}
        onBlockedByUserUuidChange={setBlockedByUserUuid}
        onConfirm={handleConfirmBlockVehicle}
        onCancel={handleCancelBlockVehicle}
        isLoading={isBlockingVehicle}
      />
    ),
    [
      blockDialogOpen,
      blockReason,
      blockDuration,
      blockedByUserUuid,
      handleConfirmBlockVehicle,
      handleCancelBlockVehicle,
      isBlockingVehicle,
    ],
  );

  return {
    getActionsForVehicle,
    dialogs,
  };
}
